import { useContext, useState } from "react";
import api from "../../api";
import { useToast } from "../ToastContext";
import { LoadingContext } from "../LoadingContext";
import { AuthContext } from "../AuthContext";

export interface DespesasVendedorType {
    id: number;
    description?: string;
    value?: number;
    date?: string;
    observation?: string;
    status?: number;
    vendedorId?: number;
    vendedorName?: string;
    tipoDespesasVendedorId?: number;
    tipoDespesasVendedorName?: string;
    tipoDespesasVendedorIcon?: string;
    hasExpensePhoto?: boolean;
}

export interface ExpenseAttachmentView {
    url: string;
    contentType: string;
    fileName: string;
}

export interface CreateDespesasVendedorType {
    description?: string;
    value?: number;
    date?: string;
    observation?: string;
    empresaId?: number;
    vendedorId?: number;
    tipoDespesasVendedorId?: number;
}

export interface UpdateDespesasVendedorType {
    id: number;
    description?: string;
    value?: number;
    date?: string;
    observation?: string;
    terceiroId?: number;
    tipoDespesasVendedorId?: number;
}

export default function useDespesasVendedor() {
    const showToast = useToast();
    const { setLoading } = useContext(LoadingContext);
    const { handleLogout } = useContext(AuthContext);
    const [listDespesasVendedor, setListDespesasVendedor] = useState<DespesasVendedorType[]>([]);
    const [listDespesasVendedorByEmpresa, setListDespesasVendedorByEmpresa] = useState<DespesasVendedorType[]>([]);
    const [despesasVendedorPageCount, setDespesasVendedorPageCount] = useState<number>(0);
    const [initialListDespesasVendedor, setInitialListDespesasVendedor] = useState<DespesasVendedorType[]>([]);
    const [despesasVendedor, setDespesasVendedor] = useState<DespesasVendedorType>({
        id: 0,
        description: "",
        value: 0,
        date: "",
        observation: "",
        status: 0,
        vendedorId: 0,
        vendedorName: "",
        tipoDespesasVendedorId: 0,
        tipoDespesasVendedorName: "",
        tipoDespesasVendedorIcon: "",
    });

    function createDespesasVendedor(createDespesasVendedorType: CreateDespesasVendedorType): Promise<CreateDespesasVendedorType | null> {
        setLoading(true);
        return api
            .post(`/DespesasVendedor/create`, createDespesasVendedorType)
            .then((response) => {
                const createdDespesasVendedor: CreateDespesasVendedorType = response.data;
                showToast("success", "Despesa criada com sucesso.");
                setLoading(false);
                return createdDespesasVendedor;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao criar despesa. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao criar despesa:", error);
                return null;
            });
    }

    function getAllDespesasVendedorPaged(
        pageNumber: number,
        pageSize: number,
        empresaId?: number,
    ): Promise<DespesasVendedorType[] | null> {
        setLoading(true);
        let url = `/DespesasVendedor/getAllDespesasVendedorPaged?pageNumber=${pageNumber}&pageSize=${pageSize}`;

        if (empresaId) {
            url += `&empresaId=${empresaId}`;
        }

        return api
            .get(url)
            .then((response) => {
                setListDespesasVendedor(response.data.result);
                setInitialListDespesasVendedor(response.data.result);
                setDespesasVendedorPageCount(response.data.pageCount);
                setLoading(false);
                return response.data.result;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao carregar despesas. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao carregar despesas:", error);
                return null;
            });
    }

    function updateDespesasVendedor(updateDespesasVendedorData: UpdateDespesasVendedorType): Promise<UpdateDespesasVendedorType | null> {
        setLoading(true);
        return api
            .put(`/DespesasVendedor/${updateDespesasVendedorData.id}`, updateDespesasVendedorData)
            .then((response) => {
                showToast("success", "Despesa atualizada com sucesso.");
                setLoading(false);
                return response.data;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao atualizar despesa. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao atualizar despesa:", error);
                return null;
            });
    }

    function deleteDespesasVendedor(despesasVendedorId: number): Promise<DespesasVendedorType | null> {
        setLoading(true);
        return api
            .delete(`/DespesasVendedor/${despesasVendedorId}`)
            .then(() => {
                setDespesasVendedor({
                    id: 0,
                    description: "",
                    value: 0,
                    date: "",
                    observation: "",
                    status: 0,
                    vendedorId: 0,
                    vendedorName: "",
                    tipoDespesasVendedorId: 0,
                    tipoDespesasVendedorName: "",
                    tipoDespesasVendedorIcon: "",
                });
                showToast("success", "Despesa deletada com sucesso.");
                setLoading(false);
                return null;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao deletar despesa. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao deletar despesa:", error);
                return null;
            });
    }

    function handleGetDespesasVendedorByEmpresaId(empresaId: number): Promise<DespesasVendedorType[] | null> {
        setLoading(true);
        return api
            .get(`/DespesasVendedor/getDespesasVendedorByEmpresaId?empresaId=${empresaId}`)
            .then((response) => {
                const despesasVendedor: DespesasVendedorType[] = response.data;
                setListDespesasVendedorByEmpresa(despesasVendedor);
                setLoading(false);
                return despesasVendedor;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao buscar informações das despesas. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao buscar informações das despesas:", error);
                return null;
            });
    }

    function aprovarDespesasVendedor(despesaId: number): Promise<boolean> {
        setLoading(true);
        return api
            .put(`/DespesasVendedor/${despesaId}/aprovar`)
            .then(() => {
                showToast("success", "Despesa aprovada com sucesso.");
                setLoading(false);
                return true;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast("error", "Sessão desconectada. Favor login novamente.", error);
                } else {
                    showToast("error", "Erro ao aprovar despesa. Verifique novamente mais tarde.", error);
                }
                setLoading(false);
                console.error("Erro ao aprovar despesa:", error);
                return false;
            });
    }

    function rejeitarDespesasVendedor(despesaId: number): Promise<boolean> {
        setLoading(true);
        return api
            .put(`/DespesasVendedor/${despesaId}/rejeitar`)
            .then(() => {
                showToast("success", "Despesa rejeitada com sucesso.");
                setLoading(false);
                return true;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast("error", "Sessão desconectada. Favor login novamente.", error);
                } else {
                    showToast("error", "Erro ao rejeitar despesa. Verifique novamente mais tarde.", error);
                }
                setLoading(false);
                console.error("Erro ao rejeitar despesa:", error);
                return false;
            });
    }

    async function fetchExpenseAttachment(despesaId: number): Promise<ExpenseAttachmentView | null> {
        const response = await api.get(`/DespesasVendedor/${despesaId}/anexo`, {
            responseType: "blob",
        });

        const contentType = String(response.headers["content-type"] || "application/octet-stream");
        const contentDisposition = String(response.headers["content-disposition"] || "");
        const match = contentDisposition.match(/filename="?([^"]+)"?/i);
        const fileName = match?.[1] || `despesa-vendedor-${despesaId}`;
        const blob = new Blob([response.data], { type: contentType });
        const url = window.URL.createObjectURL(blob);
        return { url, contentType, fileName };
    }

    async function downloadExpenseAttachment(despesaId: number) {
        const response = await api.get(`/DespesasVendedor/${despesaId}/anexo`, {
            responseType: "blob",
        });

        const contentDisposition = response.headers["content-disposition"] || "";
        const match = contentDisposition.match(/filename="?([^"]+)"?/i);
        const filename = match?.[1] || `despesa-vendedor-${despesaId}`;
        const blob = new Blob([response.data], { type: String(response.headers["content-type"] || "application/octet-stream") });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
    }



    return {
        createDespesasVendedor,
        getAllDespesasVendedorPaged,
        updateDespesasVendedor,
        deleteDespesasVendedor,
        setListDespesasVendedor,
        listDespesasVendedor,
        setDespesasVendedorPageCount,
        despesasVendedorPageCount,
        setInitialListDespesasVendedor,
        initialListDespesasVendedor,
        setDespesasVendedor,
        despesasVendedor,
        handleGetDespesasVendedorByEmpresaId,
        setListDespesasVendedorByEmpresa,
        listDespesasVendedorByEmpresa,
        aprovarDespesasVendedor,
        rejeitarDespesasVendedor,
        fetchExpenseAttachment,
        downloadExpenseAttachment,
    };
}
